import { CollisionVolume } from "./collision_volume";
import { Aabb,aabbOverlaps} from "./aabb";
import { BoundingSphere,spheresOverlap } from "./bounding_sphere";
import CollisionRenderPipeline from "../render/pipelines/collision_renderpipeline";
import Camera from "../scene/camera";
import Transform from "../render/transform";
import ShaderService from "../shader/shader_service";
import ResourceService from "../resource/resourceservice";
import { RaycastHit,Ray3 } from "./ray_types"; 
import {point_on_ray} from "./ray_math"
import JumpGraphService from "./jump_graph_service";
import GraphRenderPipeline from "../render/pipelines/graph_renderpipeline";

const RAY_EPSILON = 0.000001;

export default class CollisionService 
{
    collision_renderpipeline: CollisionRenderPipeline;
     graph_renderpipeline: GraphRenderPipeline;
     jump_graph: JumpGraphService;

    transform:Transform;
    enabled:boolean = false;
    constructor()
    {
        this.transform = new Transform();
        this.collision_renderpipeline = new CollisionRenderPipeline("collision","collision_shader",this);
        this.jump_graph = new JumpGraphService();
        this.graph_renderpipeline = new GraphRenderPipeline("graph", "graph_shader", this.jump_graph);
      }

    private volumes = new Set<CollisionVolume>();

    async build(device: GPUDevice,format: GPUTextureFormat,shader_service: ShaderService, resource_service: ResourceService): Promise<void> 
    {
         this.collision_renderpipeline.build(device,format,shader_service,resource_service);
         this.graph_renderpipeline.build(device, format, shader_service, resource_service);

    }

    prepare(device: GPUDevice, camera: Camera): void
    {
        if(this.enabled)
        {
           this.collision_renderpipeline.prepare(device,camera,this.transform);
           this.graph_renderpipeline.prepare(device, camera, this.transform);
        }
    }

  draw(pass: GPURenderPassEncoder): void 
  {
  
    if(this.enabled)
    {
        this.collision_renderpipeline.draw(pass);
        this.graph_renderpipeline.draw(pass);
    }
    
  }

  register(volume: CollisionVolume): void {
    this.volumes.add(volume);
  }

  unregister(volume: CollisionVolume): void {
    this.volumes.delete(volume);
  }

  getAll(): CollisionVolume[] {
    return Array.from(this.volumes);
  }

  querySphere(center: Float32Array, radius: number, layer?: string): CollisionVolume[] {
    const results: CollisionVolume[] = [];

    for (const volume of this.volumes) {
      if (layer && volume.layer !== layer) continue;

      const sphere = volume.getWorldSphere();
      if (!sphere) continue;

      if (spheresOverlap(center, radius, sphere.center, sphere.radius)) {
        results.push(volume);
      }
    }

    return results;
  }
  
  queryBoundingSphere(sphere: BoundingSphere, layer?: string): CollisionVolume[] {
    return this.querySphere(sphere.center, sphere.radius, layer);
  }

  queryAabb(aabb: Aabb): CollisionVolume[] {
    return Array.from(this.volumes).filter(v => {
      const other = v.getWorldAabb();
      if (!other) return false;
      return aabbOverlaps(aabb, other);
    });
  }

    queryAabbRadiusXZ(
    x: number,
    z: number,
    radius: number,
    layer?: string
  ): CollisionVolume[] {
    const results: CollisionVolume[] = [];
    const r2 = radius * radius;

    for (const volume of this.volumes) {
      if (layer && volume.layer !== layer) continue;

      const aabb = volume.getWorldAabb();
      if (!aabb) continue;

      const cx = Math.max(aabb.min[0], Math.min(x, aabb.max[0]));
      const cz = Math.max(aabb.min[2], Math.min(z, aabb.max[2]));

      const dx = cx - x;
      const dz = cz - z;

      if (dx * dx + dz * dz < r2) {
        results.push(volume);
      }
    }

    return results;
  }
  isBlockedXZ(
    x: number,
    z: number,
    radius: number,
    layer?: string
  ): boolean {
    const r2 = radius * radius;

    for (const volume of this.volumes) {
      if (layer && volume.layer !== layer) continue;

      const aabb = volume.getWorldAabb();
      if (!aabb) continue;

      const cx = Math.max(aabb.min[0], Math.min(x, aabb.max[0]));
      const cz = Math.max(aabb.min[2], Math.min(z, aabb.max[2]));

      const dx = cx - x;
      const dz = cz - z;

      if (dx * dx + dz * dz < r2) {
        return true;
      }
    }

    return false;
  }

  public raycastAabb(ray: Ray3, aabb: Aabb): RaycastHit | null {
  let tmin = -Infinity;
  let tmax = Infinity;

  for (let axis = 0; axis < 3; axis++) {
    const origin = ray.origin[axis];
    const direction = ray.direction[axis];
    const min = aabb.min[axis];
    const max = aabb.max[axis];

    if (Math.abs(direction) < RAY_EPSILON) {
      if (origin < min || origin > max) return null;
      continue;
    }

    let t1 = (min - origin) / direction;
    let t2 = (max - origin) / direction;

    if (t1 > t2) {
      const temp = t1;
      t1 = t2;
      t2 = temp;
    }

    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);

    if (tmin > tmax) return null;
  }

  if (tmax < 0) return null;

  const t = tmin >= 0 ? tmin : tmax;
  if (t < 0) return null;

  return {
    volume: null,
    point: point_on_ray(ray, t),
    distance: t,
    layer: null,
  };
}

public raycastVolumes(ray: Ray3, layer?: string): RaycastHit | null {
  let best: RaycastHit | null = null;

  for (const volume of this.volumes) {
    if (layer && volume.layer !== layer) continue;

    const aabb = volume.getWorldAabb();
    if (!aabb) continue;

    const hit = this.raycastAabb(ray, aabb);
    if (!hit) continue;

    hit.volume = volume;
    hit.layer = volume.layer;

    if (!best || hit.distance < best.distance) {
      best = hit;
    }
  }

  return best;
}

}